import React, { useState, useEffect, useContext } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import useAuth from 'providers/Auth/useAuth';

import { getCooperative } from 'services/API/Cooperative';
import {
  getCulturesByExploitationId,
  getExploitationInformations,
  getGroundTypeByExploitationId,
  getParcelsByExploitation,
} from 'services/API/Exploitation';

import { setInitialStateSwitchIsOrganic } from 'utils/tools_functions';

import ExploitationHome from 'components/pages/Exploitation/Home';
import ExploitationSimulation from 'components/pages/Exploitation/Simulation';
import ExploitationSimulationResult from 'components/pages/Exploitation/Simulation/Result';
import Parcels from './Parcels';

import { ErrorSimulationResultFallback } from 'utils/ErrorFallback';
import { sendEvent } from 'utils/Event';

import 'assets/styles/modal.style.scss';
import 'assets/styles/section.style.scss';
import CooperativeCsvImport from '../Cooperative/CsvImport';
import CooperativeImport from '../Cooperative/Import';
import LoginWiuz from '../Cooperative/LoginWiuz';
import CooperativeExploitation from '../Cooperative/Exploitation';
import Result from '../Cooperative/Result';
import Geofolia from '../Cooperative/Geofolia';
import TelepacImport from './TelepacImport';
import { ArableContext, ExploitationContext, LoadingContext } from 'utils/context';
import ExploitationHeader from './header/exploitation.header';
import CulturesDataGridExploitation from './CulturesDataGridExploitation';
import { getRandomColor, emptyCulture } from 'utils/culturesColors';
import GeofoliaProcess from '../Cooperative/GeofoliaProcess';
import RotateEffectsDataGridExploitation from './RotateEffectsDataGridExploitation';
import GroundTypeEffectsDataGridExploitation from './GroundTypeEffectsDataGridExploitation';
import ProcedureChangeCampaign from './ProcedureCampaign';

const Exploitation = () => {
  const [secteurs, setSecteurs] = useState([]);
  const { user } = useAuth();
  const cooperativeId = localStorage.getItem('cooperative');
  const idGet = cooperativeId ? cooperativeId : user.cooperative.id;
  const { setFullScreen } = useContext(LoadingContext);
  const [arableSurface, setArableSurface] = useState(0);
  const value = {
    arableSurface,
    setArableSurface,
  };
  const { setForceLoader } = useContext(LoadingContext);

  const { exploitationContext, setExploitationContext, setCulturesColors } =
    useContext(ExploitationContext);
  const [nitrogenCover, setNitrogenCover] = useState(null);
  const [nitrogenSupplyByCoverOrganic, setNitrogenSupplyByCoverOrganic] = useState(null);
  const [biomassSupplyByCover, setBiomassSupplyByCover] = useState(null);
  const [cultures, setCultures] = useState([]);
  const [groundTypes, setGroundTypes] = useState([]);

  const [exploitationId, setExploitationId] = useState(localStorage.getItem('exploitation'));

  const loadData = async () => {
    setForceLoader(true);
    if (!localStorage.getItem('exploitation')) {
      setExploitationId(localStorage.getItem('exploitation'));
      setFullScreen(true);
    }
    try {
      const response = await getCooperative(idGet);
      setInitialStateSwitchIsOrganic(response?.data?.data?.hasConventionalData);
      setSecteurs(response.data.data.sectors);
    } catch (err) {
      toast.error(err.data);
    } finally {
      setForceLoader(false);
    }
  };

  const setColorContext = (data) => {
    let getStructureColors = [];
    let userCulturesColors = data.map((c) => c.color).filter((c) => c && c !== null);
    data.forEach((culture) => {
      let randomColor = getRandomColor(userCulturesColors);
      userCulturesColors.push('#' + randomColor);
      getStructureColors.push({
        id: culture.id,
        name: culture.name,
        color: culture.color ? culture.color.replace('#', '') : randomColor,
        isArable: culture.isArable,
        irrigationWaterNeeded: culture.irrigationWaterNeeded,
      });
    });
    getStructureColors.push(emptyCulture);
    setCulturesColors(getStructureColors);
  };

  const loadDataExploitation = async (isOrganic, exploitId) => {
    if (!exploitId) {
      exploitId = localStorage.getItem('exploitation');
    }
    try {
      const results = await Promise.all([
        getExploitationInformations(exploitId),
        getCulturesByExploitationId(exploitId, isOrganic),
        getParcelsByExploitation(exploitId),
        getGroundTypeByExploitationId(exploitId),
      ]);
      const groundTypes =
        [
          ...new Map(
            results[1].data.data
              ?.flatMap((culture) => culture.groundTypeEffects || [])
              ?.map((effect) => [
                effect.groundType.id,
                { id: effect.groundType.id, name: effect.groundType.name },
              ])
          ).values(),
        ] || [];
      setExploitationContext({
        ...results[0].data.data,
        cultures: results[1].data.data.sort((a, b) => a.name.localeCompare(b.name)),
        parcels: results[2].data.data,
        groundTypes: results[3].data.data,
      });
      setArableSurface(results[0].data.data?.arableTotalSurface);
      setNitrogenCover({ nitrogenSupplyByCover: results[0].data.data?.nitrogenSupplyByCover });
      setNitrogenSupplyByCoverOrganic({
        nitrogenSupplyByCoverOrganic: results[0].data.data?.nitrogenSupplyByCoverOrganic,
      });
      setBiomassSupplyByCover({
        biomassSupplyByCover: results[0].data.data?.biomassSupplyByCover,
      });
      setCultures(results[1].data);

      setGroundTypes(groundTypes);

      if (results[1].data.data) {
        setColorContext(results[1].data.data);
      }

      return results[0];
    } catch (error) {
      toast.error(error.data);
    }
  };

  useEffect(() => {
    user.cooperative && loadData();
  }, [user]);

  // Life Hook Dismount
  useEffect(() => {
    return () => {
      sendEvent('cancelHttpRequest');
    };
  }, []);

  return (
    <div className="ExploitationContainer">
      <ArableContext.Provider value={value}>
        <ExploitationHeader />
        <Routes>
          <Route
            path="/"
            element={
              <ExploitationHome
                secteurs={secteurs}
                setExploitation={loadDataExploitation}
                exploitation={exploitationContext}
              />
            }
          />
          <Route
            path="parcels"
            element={
              <Parcels exploitation={exploitationContext} setExploitation={loadDataExploitation} />
            }
          />
          <Route
            path="cultures"
            element={
              <CulturesDataGridExploitation
                exploitation={exploitationContext}
                setExploitation={loadDataExploitation}
                structureId={idGet}
                setColorContext={setColorContext}
                user={user}
                nitrogenCover={nitrogenCover}
                setNitrogenCover={setNitrogenCover}
                nitrogenSupplyByCoverOrganic={nitrogenSupplyByCoverOrganic}
                setNitrogenSupplyByCoverOrganic={setNitrogenSupplyByCoverOrganic}
                biomassSupplyByCover={biomassSupplyByCover}
                setBiomassSupplyByCover={setBiomassSupplyByCover}
                cultures={cultures}
              />
            }
          />
          <Route
            path="groundTypesEffects"
            element={
              <GroundTypeEffectsDataGridExploitation
                exploitation={exploitationContext}
                setExploitation={loadDataExploitation}
                from={'exploitations'}
                cultures={cultures}
                groundTypes={groundTypes}
              />
            }
          />
          <Route
            path="rotateEffects"
            element={
              <RotateEffectsDataGridExploitation
                exploitation={exploitationContext}
                setExploitation={loadDataExploitation}
                from={'exploitations'}
                cultures={cultures}
              />
            }
          />
          <Route
            path="simulation"
            element={
              <ExploitationSimulation
                exploitationSelected={exploitationContext}
                setExploitation={loadDataExploitation}
              />
            }
          />
          <Route
            path="result"
            element={
              <ExploitationSimulationResult
                exploitationSelected={exploitationContext}
                setExploitation={loadDataExploitation}
                culturesFromParent={cultures?.data?.filter(
                  (culture) => culture.isCustomized === true
                )}
              />
            }
          />
          <Route path="import" element={<CooperativeImport />} />
          <Route path="import/csvImport" element={<CooperativeCsvImport />} />
          <Route path="import/loginWiuz" element={<LoginWiuz />} />
          <Route path="import/exploitation" element={<CooperativeExploitation />} />
          <Route path="import/result" element={<Result />} />
          <Route path="import/geofolia" element={<Geofolia />} />
          <Route exact path="import/geofolia/process" element={<GeofoliaProcess />} />
          <Route exact path="import/telepac" element={<TelepacImport />} />

          <Route
            path="campaign"
            element={
              <ProcedureChangeCampaign
                exploitationSelected={exploitationContext}
                loadDataExploitation={loadDataExploitation}
              />
            }
          />

          <Route element={<Navigate to="/" />} />
        </Routes>
      </ArableContext.Provider>
    </div>
  );
};

export default Exploitation;
